import React, { useState, useEffect } from 'react';
import { Shirt } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { getClientGarments, assignGarmentsToClient, getClientAssignedGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';

const AssignGarmentsModal = ({ isOpen, onClose, client, onSuccess }) => {
  const [availableGarments, setAvailableGarments] = useState([]);
  const [assignedGarments, setAssignedGarments] = useState([]);
  const [selectedGarments, setSelectedGarments] = useState([]);
  // Mapa de overrides de peso por prenda para este cliente.
  // Se hidrata con los pesos ya guardados al cargar y se actualiza al editar.
  const [customPesos, setCustomPesos] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && client) {
      loadData();
    }
  }, [isOpen, client]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Cargar prendas disponibles
      const garmentsResponse = await getClientGarments({
        query: { estado: 'Activo' },
        page: 1,
        limit: 1000
      });
      setAvailableGarments(garmentsResponse.data || []);

      // Cargar prendas ya asignadas al cliente
      try {
        const assignedResponse = await getClientAssignedGarments(client._id);
        const assigned = assignedResponse.data || [];
        setAssignedGarments(assigned);
        setSelectedGarments(assigned.map(g => ({ ...g })));
        // Hidratar overrides de peso con los valores ya guardados para este cliente.
        const initial = {};
        assigned.forEach(g => {
          if (g?.prendaId != null && g?.peso != null) {
            initial[g.prendaId] = Number(g.peso);
          }
        });
        setCustomPesos(initial);
      } catch (error) {
        // Si no hay prendas asignadas, continuar normalmente
        setAssignedGarments([]);
        setSelectedGarments([]);
        setCustomPesos({});
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas disponibles',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleGarment = (garment) => {
    const isAlreadySelected = selectedGarments.some(g => g.prendaId === garment._id);

    if (isAlreadySelected) {
      // Si ya está seleccionada, la removemos
      setSelectedGarments(selectedGarments.filter(g => g.prendaId !== garment._id));
    } else {
      // Si no está seleccionada, la agregamos usando el peso override si existe,
      // o el peso base de la prenda como fallback.
      const pesoParaCliente = customPesos[garment._id] != null
        ? Number(customPesos[garment._id])
        : Number(garment.peso) || 0;
      setSelectedGarments([
        ...selectedGarments,
        {
          prendaId: garment._id,
          nombre: garment.nombre,
          peso: pesoParaCliente,
          areaAlistamiento: garment.areaAlistamiento,
        },
      ]);
    }
  };

  const handlePesoChange = (prendaId, rawValue) => {
    // Permitimos string vacío para borrar mientras se escribe, pero bloqueamos
    // valores negativos. El guardado al backend exige número.
    let next = rawValue;
    if (next === '' || next === '-') {
      next = 0;
    } else {
      const parsed = Number(next);
      if (!Number.isFinite(parsed) || parsed < 0) return;
      next = parsed;
    }
    // Persistimos tanto en customPesos (memoria) como en selectedGarments (lo que se va a guardar).
    setCustomPesos(prev => ({ ...prev, [prendaId]: next }));
    setSelectedGarments(prev => prev.map(g =>
      g.prendaId === prendaId ? { ...g, peso: next } : g
    ));
  };

  const handleSave = async () => {
    if (selectedGarments.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Debes seleccionar al menos una prenda',
        variant: 'default',
      });
      return;
    }

    setIsSaving(true);
    try {
      await assignGarmentsToClient(client._id, selectedGarments);
      toast({
        title: 'Éxito',
        description: 'Prendas asignadas correctamente al cliente',
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron asignar las prendas',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredGarments = availableGarments.filter(garment =>
    garment.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shirt className="h-5 w-5 text-blue-600" />
            Asignar Prendas a Cliente
          </DialogTitle>
          <DialogDescription>
            Cliente: <strong>{client?.nombre}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Prendas Disponibles */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">
                Selecciona las prendas para este cliente
              </h3>
              <Badge variant="secondary" className="text-xs">
                {selectedGarments.length} seleccionada{selectedGarments.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <Input
              type="text"
              placeholder="Buscar prenda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            {isLoading ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Cargando prendas...</p>
                </CardContent>
              </Card>
            ) : filteredGarments.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-sm text-gray-500">
                  {searchTerm ? 'No se encontraron prendas' : 'No hay prendas disponibles'}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
                {filteredGarments.map((garment) => {
                  const isSelected = selectedGarments.some(g => g.prendaId === garment._id);
                  const selectedItem = selectedGarments.find(g => g.prendaId === garment._id);
                  // Peso editable: si está seleccionada, partimos del valor override
                  // (o del peso guardado). Si no, mostramos solo el base como referencia.
                  const editablePeso = isSelected
                    ? (selectedItem?.peso ?? customPesos[garment._id] ?? garment.peso)
                    : null;
                  const isCustomizado = isSelected && (
                    (customPesos[garment._id] != null
                      && customPesos[garment._id] !== garment.peso)
                    || (selectedItem?.peso != null && selectedItem.peso !== garment.peso)
                  );
                  return (
                    <Card
                      key={garment._id}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'hover:border-blue-200 hover:shadow-sm'
                      }`}
                      onClick={() => handleToggleGarment(garment)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-sm truncate flex-1 min-w-0">
                            {garment.nombre}
                          </div>
                          {isSelected && (
                            <Badge className="bg-blue-600 text-white text-xs">
                              ✓ Seleccionada
                            </Badge>
                          )}
                        </div>
                        {garment.areaAlistamiento && (
                          <div className="text-xs text-blue-600 mb-2">
                            {garment.areaAlistamiento === 'toalla' && '🧺 Toalla'}
                            {garment.areaAlistamiento === 'lenceria' && '🛏️ Lencería'}
                            {garment.areaAlistamiento === 'ambos' && '🧺🛏️ Ambos'}
                          </div>
                        )}
                        {isSelected ? (
                          <div
                            className="space-y-1"
                            // Evitamos que clicks en el input disparen el toggle del card.
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                              Peso para este cliente (g)
                            </label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                value={editablePeso ?? ''}
                                onChange={(e) => handlePesoChange(garment._id, e.target.value)}
                                className="h-7 text-xs"
                                placeholder="Peso"
                              />
                              <span className="text-xs text-gray-500">g</span>
                              {isCustomizado && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-300 text-amber-700 bg-amber-50">
                                  custom
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-400">
                              Base: {garment.peso}g
                            </p>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">
                            Peso base: <span className="font-medium">{garment.peso}g</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || selectedGarments.length === 0}>
            {isSaving ? 'Guardando...' : 'Guardar Asignación'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignGarmentsModal;
